import type { UIMessage } from 'ai';

// PROBLEM - ONLY MESSAGES ARE COUNTED HERE, NOT THE CODEBASE.
export const MAX_TOKEN_LIMIT = 100_000; // To be 1L
export const SYSTEM_PROMPT_TOKENS = 6_000; // Base tokens reserved for system prompt

const SUMMARY_MARKER = 'BELOW_IS_A_SUMMARY_OF_THE_CHAT_HISTORY';

type ContentType = {
  type: 'text' | 'image';
  text?: string;
  image?: string;
};

/**
 * Calculate rough token size from messages
 * This is a client-side version that estimates tokens based on character count
 * Uses the same logic as the server-side version: message.content.length / 4
 */
export function getRoughTokenSize(messages: UIMessage[]): number {
  // Only consider messages AFTER the summary delimiter (searching from the end)
  let sourceMessages = messages;

  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i] as unknown as { role?: string; content?: unknown };

    if (m?.role === 'user' && typeof m.content === 'string' && m.content.trim() === SUMMARY_MARKER) {
      sourceMessages = messages.slice(i + 1);
      break;
    }
  }

  let roughTokenSize = 0;

  sourceMessages.forEach((message) => {
    if (typeof message.content === 'string') {
      roughTokenSize += message.content.length / 4;
    } else if (Array.isArray(message.content)) {
      // Handle array of content types
      const contentArray = message.content as ContentType[];
      contentArray.forEach((contentItem: ContentType) => {
        if (contentItem.type === 'text' && typeof contentItem.text === 'string') {
          roughTokenSize += contentItem.text.length / 4;
        }
      });
    }
  });

  // Include system prompt tokens in the total
  return Math.round(roughTokenSize + SYSTEM_PROMPT_TOKENS);
}

/**
 * Calculate the percentage of tokens used relative to the max limit
 */
export function getTokenPercentage(messages: UIMessage[]): number {
  const tokenSize = getRoughTokenSize(messages);
  const percentage = (tokenSize / MAX_TOKEN_LIMIT) * 100;
  return Math.min(Math.ceil(percentage), 100); // Ceil to whole number, cap at 100%
}

/**
 * Format token count with comma separators
 */
export function formatTokenCount(count: number): string {
  return count.toLocaleString();
}
